// AI Ghostwriter Pro - Dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { trackConversion } from '../utils/referral';
import { 
  BookOpen, Plus, FileText, CheckCircle, Clock, AlertCircle, 
  Loader2, PenTool, Eye, Download, RefreshCw, ChevronRight,
  Sparkles, Target, Edit, Send, CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function GhostwriterDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Modal states
  const [showNewProject, setShowNewProject] = useState(false);
  const [showPackageSelect, setShowPackageSelect] = useState(false);
  const [showOutlineReview, setShowOutlineReview] = useState(false);
  const [showChapterView, setShowChapterView] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  
  // Form states
  const [projectForm, setProjectForm] = useState({
    title: '',
    genre: '',
    subgenre: '',
    target_audience: '',
    word_count_target: 30000,
    chapter_count: 15,
    tone: 'engaging and accessible',
    description: '',
    special_instructions: '',
    tropes: ''
  });
  const [selectedPackage, setSelectedPackage] = useState('professional');
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  
  // Status
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
    handlePaymentReturn();
  }, []);

  const loadData = async () => {
    try {
      const [projectsRes, packagesRes, statsRes] = await Promise.all([
        api.request('/ghostwriter/projects'),
        api.request('/ghostwriter/packages'),
        api.request('/ghostwriter/stats')
      ]);
      
      setProjects(projectsRes.projects || []);
      setPackages(packagesRes.packages || []);
      setStats(statsRes);
      
      // Fetch PayPal client ID
      try {
        const ppRes = await fetch(`${API_URL}/api/paypal/client-id`);
        const ppData = await ppRes.json();
        if (ppData.client_id) setPaypalClientId(ppData.client_id);
      } catch (e) { /* PayPal optional */ }
      
      // Check for package in URL
      const pkgParam = searchParams.get('package');
      if (pkgParam && packagesRes.packages.find(p => p.id === pkgParam)) {
        setSelectedPackage(pkgParam);
        setShowNewProject(true);
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentReturn = async () => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const method = searchParams.get('method');
    
    if (payment === 'success' && method === 'paypal') {
      setSuccess('PayPal payment successful! Your project is ready.');
      // Track affiliate conversion for PayPal
      const pkg = packages.find(p => p.id === searchParams.get('package_id')) || {};
      trackConversion(pkg.price || 0, 'ghostwriter');
      loadData();
      window.history.replaceState({}, '', '/ghostwriter/dashboard');
    } else if (payment === 'success' && sessionId) {
      setSuccess('Processing payment...');
      try {
        const result = await api.request(`/ghostwriter/payment/status/${sessionId}`);
        setSuccess('Payment successful! Your project is ready.');
        
        // Track affiliate conversion for Stripe
        if (result.amount) {
          trackConversion(result.amount, 'ghostwriter');
        }

        if (result.project_id) {
          await loadProjectDetails(result.project_id);
        }
        
        loadData();
        window.history.replaceState({}, '', '/ghostwriter/dashboard');
      } catch (err) {
        setError('Payment verification failed. Please contact support.');
      }
    } else if (payment === 'cancelled') {
      setError('Payment was cancelled.');
      window.history.replaceState({}, '', '/ghostwriter/dashboard');
    }
  };

  const loadProjectDetails = async (projectId) => {
    try {
      const response = await api.request(`/ghostwriter/projects/${projectId}`);
      setSelectedProject(response.project);
      setChapters(response.chapters || []);
    } catch (err) {
      setError('Failed to load project: ' + err.message);
    }
  };

  const handlePurchasePackage = async () => {
    setProcessing(true);
    setError('');
    
    // Apply promo code first if valid
    if (promoResult?.valid) {
      try {
        await api.request('/promo/apply', {
          method: 'POST',
          body: JSON.stringify({
            code: promoCode,
            product: 'ghostwriter',
            package_id: selectedPackage,
            original_price: packages.find(p => p.id === selectedPackage)?.price || 0
          })
        });
      } catch (e) {
        // Continue even if apply fails (validation already passed)
      }
    }
    
    try {
      const response = await api.request('/ghostwriter/purchase', {
        method: 'POST',
        body: JSON.stringify({
          package_id: selectedPackage,
          host_url: window.location.origin,
          promo_code: promoResult?.valid ? promoCode : null,
          project_data: {
            ...projectForm,
            tropes: projectForm.tropes ? projectForm.tropes.split(',').map(t => t.trim()) : []
          }
        })
      });
      
      window.location.href = response.url;
    } catch (err) {
      setError('Failed to start checkout: ' + err.message);
      setProcessing(false);
    }
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoResult(null);
    try {
      const pkg = packages.find(p => p.id === selectedPackage);
      const result = await fetch(`${API_URL}/api/promo/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: promoCode,
          product: 'ghostwriter',
          package_id: selectedPackage,
          original_price: pkg?.price || 0
        })
      });
      const data = await result.json();
      if (result.ok) {
        setPromoResult(data);
      } else {
        setPromoResult({ valid: false, error: data.detail || 'Invalid code' });
      }
    } catch {
      setPromoResult({ valid: false, error: 'Failed to validate code' });
    } finally {
      setPromoLoading(false);
    }
  };

  const handleGenerateOutline = async () => {
    if (!selectedProject) return;
    setProcessing(true);
    setError('');
    
    try {
      await api.request(`/ghostwriter/projects/${selectedProject.id}/generate-outline`, {
        method: 'POST'
      });
      setSuccess('Outline generation started! This may take a few minutes.');
      
      // Poll for completion
      pollProjectStatus(selectedProject.id);
    } catch (err) {
      setError('Failed to generate outline: ' + err.message);
      setProcessing(false);
    }
  };

  const pollProjectStatus = async (projectId) => {
    const interval = setInterval(async () => {
      try {
        const response = await api.request(`/ghostwriter/projects/${projectId}`);
        setSelectedProject(response.project);
        setChapters(response.chapters || []);
        
        if (!['generating_outline', 'writing', 'revising'].includes(response.project.status)) {
          clearInterval(interval);
          setProcessing(false);
          
          if (response.project.status === 'outline_review') {
            setSuccess('Outline ready for review!');
            setShowOutlineReview(true);
          } else if (response.project.status === 'review') {
            setSuccess('Your book is ready for review!');
          } else if (response.project.status === 'complete') {
            setSuccess('Your book is complete!');
          }
        }
      } catch (err) {
        clearInterval(interval);
        setProcessing(false);
      }
    }, 5000);
  };

  const handleApproveOutline = async (approved) => {
    if (!selectedProject) return;
    setProcessing(true);
    
    try {
      await api.request(`/ghostwriter/projects/${selectedProject.id}/approve-outline`, {
        method: 'POST',
        body: JSON.stringify({
          approved,
          feedback: approved ? null : revisionFeedback
        })
      });
      
      setShowOutlineReview(false);
      
      if (approved) {
        setSuccess('Outline approved! Writing has begun...');
        pollProjectStatus(selectedProject.id);
      } else {
        setSuccess('Feedback received. Please regenerate outline.');
        await loadProjectDetails(selectedProject.id);
        setProcessing(false);
      }
    } catch (err) {
      setError('Failed: ' + err.message);
      setProcessing(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!selectedProject || !revisionFeedback) return;
    setProcessing(true);
    
    try {
      await api.request(`/ghostwriter/projects/${selectedProject.id}/request-revision`, {
        method: 'POST',
        body: JSON.stringify({
          chapter_id: selectedChapter?.id || null,
          feedback: revisionFeedback,
          specific_changes: []
        })
      });
      
      setShowRevisionModal(false);
      setRevisionFeedback('');
      setSuccess('Revision requested! Processing...');
      pollProjectStatus(selectedProject.id);
    } catch (err) {
      setError('Failed: ' + err.message);
      setProcessing(false);
    }
  };

  const handleFinalizeProject = async () => {
    if (!selectedProject) return;
    setProcessing(true);
    
    try {
      const result = await api.request(`/ghostwriter/projects/${selectedProject.id}/finalize`, {
        method: 'POST'
      });
      
      setSuccess(`Book finalized! ${result.word_count} words across ${result.chapter_count} chapters.`);
      await loadProjectDetails(selectedProject.id);
      setProcessing(false);
    } catch (err) {
      setError('Failed: ' + err.message);
      setProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedProject) return;
    
    try {
      const result = await api.request(`/ghostwriter/projects/${selectedProject.id}/download`);
      
      // Create download
      const blob = new Blob([result.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_details: { color: 'bg-yellow-100 text-yellow-800', text: 'Needs Details' },
      outline_generation: { color: 'bg-blue-100 text-blue-800', text: 'Ready for Outline' },
      generating_outline: { color: 'bg-blue-100 text-blue-800', text: 'Generating Outline...' },
      outline_review: { color: 'bg-purple-100 text-purple-800', text: 'Review Outline' },
      writing: { color: 'bg-indigo-100 text-indigo-800', text: 'Writing...' },
      review: { color: 'bg-green-100 text-green-800', text: 'Ready for Review' },
      revising: { color: 'bg-orange-100 text-orange-800', text: 'Revising...' },
      complete: { color: 'bg-green-100 text-green-800', text: 'Complete' },
      outline_error: { color: 'bg-red-100 text-red-800', text: 'Error' },
      revision_error: { color: 'bg-red-100 text-red-800', text: 'Revision Error' }
    };
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>{badge.text}</span>;
  };

  const genres = [
    'Romance', 'Mystery', 'Thriller', 'Science Fiction', 'Fantasy', 
    'Horror', 'Literary Fiction', 'Historical Fiction', 'Self-Help',
    'Business', 'Biography', 'How-To', 'Health & Fitness', 'Parenting'
  ];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <PenTool className="w-8 h-8 text-purple-600" />
                AI Ghostwriter Pro
              </h1>
              <p className="text-gray-600">Manage your ghostwriting projects</p>
            </div>
            <Button 
              onClick={() => setShowNewProject(true)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
          </div>

          {/* Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
              <button onClick={() => setError('')} className="ml-auto">×</button>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              {success}
              <button onClick={() => setSuccess('')} className="ml-auto">×</button>
            </div>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-purple-600">{stats.total_projects}</div>
                  <div className="text-gray-600">Total Projects</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-green-600">{stats.completed}</div>
                  <div className="text-gray-600">Completed</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-blue-600">{stats.in_progress}</div>
                  <div className="text-gray-600">In Progress</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-orange-600">{stats.total_words_written?.toLocaleString()}</div>
                  <div className="text-gray-600">Words Written</div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6">
            {/* Projects List */}
            <div className="space-y-4">
              <h2 className="font-semibold text-lg">Your Projects</h2>
              {projects.length > 0 ? (
                projects.map(project => (
                  <Card 
                    key={project.id}
                    className={`cursor-pointer transition-all ${selectedProject?.id === project.id ? 'ring-2 ring-purple-500' : 'hover:shadow-md'}`}
                    onClick={() => loadProjectDetails(project.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold truncate">{project.title || 'Untitled Project'}</div>
                        {getStatusBadge(project.status)}
                      </div>
                      <div className="text-sm text-gray-600">{project.genre} • {project.package_name}</div>
                      {project.progress !== undefined && project.status === 'writing' && (
                        <div className="mt-2">
                          <Progress value={project.progress} className="h-2" />
                          <div className="text-xs text-gray-500 mt-1">{project.progress}% complete</div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-gray-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No projects yet. Start your first book!</p>
                    <Button onClick={() => setShowNewProject(true)} className="mt-4">
                      <Plus className="w-4 h-4 mr-2" /> Create Project
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Selected Project Details */}
            <div className="md:col-span-2">
              {selectedProject ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{selectedProject.title || 'Untitled Project'}</CardTitle>
                        <CardDescription>
                          {selectedProject.genre} • {selectedProject.word_limit?.toLocaleString()} word limit
                        </CardDescription>
                      </div>
                      {getStatusBadge(selectedProject.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Action Buttons based on status */}
                    <div className="flex gap-3 mb-6">
                      {selectedProject.status === 'pending_details' && (
                        <Button onClick={() => setShowNewProject(true)}>
                          <Edit className="w-4 h-4 mr-2" /> Complete Details
                        </Button>
                      )}
                      {selectedProject.status === 'outline_generation' && (
                        <Button onClick={handleGenerateOutline} disabled={processing}>
                          {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                          Generate Outline
                        </Button>
                      )}
                      {selectedProject.status === 'outline_review' && (
                        <Button onClick={() => setShowOutlineReview(true)}>
                          <Eye className="w-4 h-4 mr-2" /> Review Outline
                        </Button>
                      )}
                      {selectedProject.status === 'review' && (
                        <>
                          <Button onClick={() => setShowRevisionModal(true)} variant="outline">
                            <RefreshCw className="w-4 h-4 mr-2" /> Request Revision
                          </Button>
                          <Button onClick={handleFinalizeProject} disabled={processing}>
                            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            Finalize Book
                          </Button>
                        </>
                      )}
                      {selectedProject.status === 'complete' && (
                        <Button onClick={handleDownload}>
                          <Download className="w-4 h-4 mr-2" /> Download Manuscript
                        </Button>
                      )}
                    </div>

                    {/* Project Info */}
                    {selectedProject.blurb && (
                      <div className="mb-6 p-4 bg-purple-50 rounded-lg">
                        <div className="font-semibold text-purple-800 mb-2">Book Blurb</div>
                        <p className="text-gray-700">{selectedProject.blurb}</p>
                      </div>
                    )}

                    {/* Chapters List */}
                    {chapters.length > 0 && (
                      <div>
                        <div className="font-semibold mb-3">Chapters ({chapters.length})</div>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {chapters.map(chapter => (
                            <div 
                              key={chapter.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setSelectedChapter(chapter);
                                setShowChapterView(true);
                              }}
                            >
                              <div className="flex-1">
                                <div className="font-medium">
                                  Chapter {chapter.chapter_number}: {chapter.title}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {chapter.word_count ? `${chapter.word_count} words` : chapter.summary?.substring(0, 60) + '...'}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {chapter.status === 'complete' ? (
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                ) : chapter.status === 'outlined' ? (
                                  <Clock className="w-5 h-5 text-blue-500" />
                                ) : (
                                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                                )}
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Progress bar for writing status */}
                    {selectedProject.status === 'writing' && selectedProject.progress !== undefined && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">Writing Progress</span>
                          <span>{selectedProject.progress}%</span>
                        </div>
                        <Progress value={selectedProject.progress} className="h-3" />
                        <p className="text-sm text-gray-500 mt-2">
                          AI is writing your book. This typically takes 1-2 hours for a full manuscript.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center text-gray-500">
                    Select a project to view details, or create a new one
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        {/* New Project Modal */}
        <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Book Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Package Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Select Package *</label>
                <div className="grid grid-cols-3 gap-3">
                  {packages.map(pkg => (
                    <div 
                      key={pkg.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-all ${selectedPackage === pkg.id ? 'border-purple-500 bg-purple-50' : 'hover:border-gray-400'}`}
                      onClick={() => setSelectedPackage(pkg.id)}
                    >
                      <div className="font-semibold">{pkg.name}</div>
                      <div className="text-2xl font-bold text-purple-600">${pkg.price}</div>
                      <div className="text-xs text-gray-500">Up to {pkg.word_limit?.toLocaleString()} words</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Book Title *</label>
                  <Input 
                    value={projectForm.title}
                    onChange={e => setProjectForm({...projectForm, title: e.target.value})}
                    placeholder="Enter your book title"
                    data-testid="project-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Genre *</label>
                  <Select value={projectForm.genre} onValueChange={v => setProjectForm({...projectForm, genre: v})}>
                    <SelectTrigger><SelectValue placeholder="Select genre" /></SelectTrigger>
                    <SelectContent>
                      {genres.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Subgenre</label>
                  <Input 
                    value={projectForm.subgenre}
                    onChange={e => setProjectForm({...projectForm, subgenre: e.target.value})}
                    placeholder="e.g., Cozy Mystery"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Target Audience</label>
                  <Input 
                    value={projectForm.target_audience}
                    onChange={e => setProjectForm({...projectForm, target_audience: e.target.value})}
                    placeholder="e.g., Women 25-45"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Tone/Style</label>
                  <Input 
                    value={projectForm.tone}
                    onChange={e => setProjectForm({...projectForm, tone: e.target.value})}
                    placeholder="e.g., witty and fast-paced"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Target Word Count</label>
                  <Input 
                    type="number"
                    value={projectForm.word_count_target}
                    onChange={e => setProjectForm({...projectForm, word_count_target: parseInt(e.target.value)})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Number of Chapters</label>
                  <Input 
                    type="number"
                    value={projectForm.chapter_count}
                    onChange={e => setProjectForm({...projectForm, chapter_count: parseInt(e.target.value)})}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Book Description *</label>
                  <Textarea 
                    value={projectForm.description}
                    onChange={e => setProjectForm({...projectForm, description: e.target.value})}
                    placeholder="Describe your book concept, plot, or main themes..."
                    rows={4}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Tropes/Themes (comma-separated)</label>
                  <Input 
                    value={projectForm.tropes}
                    onChange={e => setProjectForm({...projectForm, tropes: e.target.value})}
                    placeholder="e.g., Enemies to Lovers, Second Chance, Found Family"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Special Instructions</label>
                  <Textarea 
                    value={projectForm.special_instructions}
                    onChange={e => setProjectForm({...projectForm, special_instructions: e.target.value})}
                    placeholder="Any specific requests or requirements..."
                    rows={2}
                  />
                </div>
              </div>

              <div className="pt-4 border-t space-y-3">
                {/* Promo Code */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Promo code (e.g. MYBOOK50)"
                    value={promoCode}
                    onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); }}
                    className="flex-1"
                    data-testid="promo-code-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleValidatePromo}
                    disabled={!promoCode.trim() || promoLoading}
                    data-testid="promo-apply-btn"
                  >
                    {promoLoading ? 'Checking...' : 'Apply'}
                  </Button>
                </div>
                {promoResult && (
                  <div className={`text-sm px-3 py-2 rounded-lg ${promoResult.valid ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`} data-testid="promo-result">
                    {promoResult.valid 
                      ? `${promoResult.description} — You save $${promoResult.discount_amount}! New price: $${promoResult.final_price}`
                      : promoResult.error}
                  </div>
                )}

                {/* Payment Method Toggle */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('stripe')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'stripe'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                    data-testid="payment-method-stripe"
                  >
                    <CreditCard className="w-5 h-5" />
                    <span className="font-medium">Credit Card</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('paypal')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'paypal'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                    data-testid="payment-method-paypal"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/></svg>
                    <span className="font-medium">PayPal</span>
                  </button>
                </div>
                
                {/* Stripe Button */}
                {paymentMethod === 'stripe' && (
                  <Button 
                    onClick={handlePurchasePackage}
                    disabled={!projectForm.title || !projectForm.genre || !projectForm.description || processing}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    data-testid="purchase-project-btn"
                  >
                    {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Pay with Card - ${packages.find(p => p.id === selectedPackage)?.price || 0}
                  </Button>
                )}
                
                {/* PayPal Button */}
                {paymentMethod === 'paypal' && paypalClientId && (
                  <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'USD' }}>
                    <PayPalButtons
                      style={{ layout: 'horizontal', color: 'blue', shape: 'rect', label: 'pay', height: 45 }}
                      disabled={!projectForm.title || !projectForm.genre || !projectForm.description || processing}
                      createOrder={async () => {
                        setProcessing(true);
                        try {
                          const resp = await api.request('/paypal/create-order', {
                            method: 'POST',
                            body: JSON.stringify({
                              product: 'ghostwriter',
                              package_id: selectedPackage,
                              host_url: window.location.origin
                            })
                          });
                          return resp.order_id;
                        } catch (err) {
                          setError('Failed to create PayPal order: ' + err.message);
                          setProcessing(false);
                          throw err;
                        }
                      }}
                      onApprove={async (data) => {
                        try {
                          const result = await api.request('/paypal/capture-order', {
                            method: 'POST',
                            body: JSON.stringify({
                              order_id: data.orderID,
                              product: 'ghostwriter',
                              package_id: selectedPackage
                            })
                          });
                          setSuccess('PayPal payment successful! Your project is ready.');
                          // Track affiliate conversion
                          const pkg = packages.find(p => p.id === selectedPackage);
                          if (pkg) trackConversion(pkg.price, 'ghostwriter');
                          setShowNewProject(false);
                          loadData();
                        } catch (err) {
                          setError('PayPal payment capture failed: ' + err.message);
                        } finally {
                          setProcessing(false);
                        }
                      }}
                      onCancel={() => {
                        setProcessing(false);
                        setError('PayPal checkout was cancelled.');
                      }}
                      onError={(err) => {
                        setProcessing(false);
                        setError('PayPal error. Please try again.');
                      }}
                    />
                  </PayPalScriptProvider>
                )}
                {paymentMethod === 'paypal' && !paypalClientId && (
                  <p className="text-sm text-gray-500 text-center py-2">PayPal is loading...</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Outline Review Modal */}
        <Dialog open={showOutlineReview} onOpenChange={setShowOutlineReview}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Review Your Book Outline</DialogTitle>
            </DialogHeader>
            {selectedProject?.outline && (
              <div className="space-y-6">
                {selectedProject.blurb && (
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="font-semibold mb-2">Book Blurb</div>
                    <p>{selectedProject.blurb}</p>
                  </div>
                )}

                {selectedProject.subtitles && (
                  <div>
                    <div className="font-semibold mb-2">Subtitle Options</div>
                    <ul className="list-disc list-inside space-y-1">
                      {selectedProject.subtitles.map((sub, i) => <li key={i}>{sub}</li>)}
                    </ul>
                  </div>
                )}

                <div>
                  <div className="font-semibold mb-3">Chapter Outline</div>
                  <div className="space-y-3">
                    {chapters.map(chapter => (
                      <div key={chapter.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="font-medium">Chapter {chapter.chapter_number}: {chapter.title}</div>
                        <p className="text-sm text-gray-600 mt-1">{chapter.summary}</p>
                        {chapter.key_points?.length > 0 && (
                          <ul className="mt-2 text-xs text-gray-500 list-disc list-inside">
                            {chapter.key_points.map((point, i) => <li key={i}>{point}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t">
                  <div className="flex gap-3">
                    <Button onClick={() => handleApproveOutline(true)} disabled={processing} className="flex-1 bg-green-600 hover:bg-green-700">
                      {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Approve & Start Writing
                    </Button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Or request changes:</label>
                    <Textarea 
                      value={revisionFeedback}
                      onChange={e => setRevisionFeedback(e.target.value)}
                      placeholder="Describe what you'd like changed in the outline..."
                      rows={3}
                    />
                    <Button 
                      onClick={() => handleApproveOutline(false)} 
                      disabled={!revisionFeedback || processing}
                      variant="outline"
                      className="mt-2"
                    >
                      Request Changes
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Chapter View Modal */}
        <Dialog open={showChapterView} onOpenChange={setShowChapterView}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Chapter {selectedChapter?.chapter_number}: {selectedChapter?.title}
              </DialogTitle>
            </DialogHeader>
            {selectedChapter && (
              <div className="space-y-4">
                {selectedChapter.content ? (
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                    {selectedChapter.content}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="font-semibold mb-2">Chapter Summary</div>
                    <p>{selectedChapter.summary}</p>
                    {selectedChapter.key_points?.length > 0 && (
                      <>
                        <div className="font-semibold mt-4 mb-2">Key Points</div>
                        <ul className="list-disc list-inside">
                          {selectedChapter.key_points.map((point, i) => <li key={i}>{point}</li>)}
                        </ul>
                      </>
                    )}
                  </div>
                )}
                
                {selectedChapter.content && selectedProject?.status === 'review' && (
                  <div className="pt-4 border-t">
                    <Button 
                      onClick={() => {
                        setShowChapterView(false);
                        setShowRevisionModal(true);
                      }}
                      variant="outline"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" /> Request Revision for This Chapter
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Revision Request Modal */}
        <Dialog open={showRevisionModal} onOpenChange={setShowRevisionModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Request Revision</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                {selectedChapter 
                  ? `Requesting revision for Chapter ${selectedChapter.chapter_number}: ${selectedChapter.title}`
                  : 'Requesting revision for the entire book'}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">What would you like changed?</label>
                <Textarea 
                  value={revisionFeedback}
                  onChange={e => setRevisionFeedback(e.target.value)}
                  placeholder="Describe the changes you'd like made..."
                  rows={4}
                />
              </div>

              {selectedProject?.revisions_remaining !== undefined && (
                <div className="text-sm text-gray-500">
                  Revisions remaining: {selectedProject.revisions_remaining === -1 ? 'Unlimited' : selectedProject.revisions_remaining}
                </div>
              )}

              <Button 
                onClick={handleRequestRevision}
                disabled={!revisionFeedback || processing}
                className="w-full"
              >
                {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Revision Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default GhostwriterDashboard;
